import { createFileRoute, Link } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { AccountHeader } from '@/components/account-nav'
import { Button } from '@/components/ui/button'
import {
  ApiError,
  billingReasonText,
  checkoutApprovalUrl,
  formatCents,
  getBillingStatus,
  getMeOptional,
  postCheckout,
  postSubscribe,
  type AuthUser,
  type BillingStatus,
  type CheckoutResponse,
} from '@/lib/kwykedit-api'

export const Route = createFileRoute('/pricing')({
  component: PricingPage,
})

function PricingPage() {
  const [me, setMe] = useState<AuthUser | null | undefined>(undefined)
  const [status, setStatus] = useState<BillingStatus | null>(null)
  const [code, setCode] = useState('')
  const [quote, setQuote] = useState<CheckoutResponse | null>(null)
  const [pending, setPending] = useState(false)
  const [paying, setPaying] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    void (async () => {
      const user = await getMeOptional().catch(() => null)
      if (!live) return
      setMe(user)
      if (user) {
        const s = await getBillingStatus().catch(() => null)
        if (live) setStatus(s)
      }
    })()
    return () => {
      live = false
    }
  }, [])

  const fetchQuote = (discountCode: string) => {
    if (pending) return
    setPending(true)
    setError(null)
    void postCheckout(discountCode)
      .then(setQuote)
      .catch((err: unknown) => {
        setQuote(null)
        setError(err instanceof ApiError ? err.message : 'Could not load payment options.')
      })
      .finally(() => setPending(false))
  }

  // Load payment options immediately for signed-in users so the PayPal
  // buttons are visible without touching the discount box.
  useEffect(() => {
    if (me && !quote && !pending && !error) fetchQuote('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me])

  const applyCode = () => fetchQuote(code.trim())

  // Continue creates the PayPal subscription (one object per deliberate
  // purchase) then hands off to PayPal approval.
  const subscribe = () => {
    if (paying) return
    setPaying(true)
    setError(null)
    void postSubscribe(code.trim())
      .then((q) => {
        const url = checkoutApprovalUrl(q)
        if (!url) throw new ApiError(502, 'PayPal did not return an approval link.')
        window.location.href = url
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Could not start checkout.')
      })
      .finally(() => setPaying(false))
  }

  const firstMonth = quote?.first_month_cents

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AccountHeader />
      <div className="mx-auto max-w-2xl px-6 py-12">
        <h1 className="mb-2 text-center text-3xl font-bold">Simple pricing</h1>
        <p className="mb-8 text-center text-sm text-muted-foreground">
          One plan. Cancel anytime.
        </p>

        {me && status && status.has_access && (
          <div className="mb-6 rounded-xl border border-border bg-card p-4 text-center text-sm">
            {billingReasonText(status)}{' '}
            <Link to="/projects" className="underline hover:opacity-80">
              Open the editor →
            </Link>
          </div>
        )}

        <div className="rounded-xl border border-border bg-card p-8 text-center">
          <p className="text-sm font-medium text-muted-foreground">KWYKEDIT</p>
          <p className="mt-2 text-5xl font-bold">
            $3<span className="text-lg font-medium text-muted-foreground">/mo</span>
          </p>
          <ul className="mx-auto mt-6 max-w-xs space-y-2 text-left text-sm text-muted-foreground">
            <li>· Full multi-track editor in your browser</li>
            <li>· All effects, keyframes & export formats</li>
            <li>· Cancel anytime</li>
          </ul>

          <div className="mt-6 border-t border-border pt-6">
            <label htmlFor="discount-code" className="text-sm font-medium">
              Discount code
            </label>
            <div className="mt-2 flex gap-2">
              <input
                id="discount-code"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="e.g. FRIEND20"
                className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm uppercase placeholder:normal-case placeholder:text-muted-foreground focus:border-ring focus:outline-none"
              />
              <Button onClick={applyCode} disabled={pending || me === null}>
                {pending ? 'Checking…' : 'Apply'}
              </Button>
            </div>
            {me === null && (
              <p className="mt-2 text-xs text-muted-foreground">
                <Link to="/login" className="underline hover:opacity-80">
                  Log in
                </Link>{' '}
                or{' '}
                <Link to="/signup" className="underline hover:opacity-80">
                  sign up
                </Link>{' '}
                to use a discount code.
              </p>
            )}
            {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
            {quote && firstMonth !== undefined && (
              <p className="mt-3 rounded-md border border-border bg-background p-3 text-sm">
                First month:{' '}
                <strong>
                  {formatCents(firstMonth)}
                  {firstMonth === 0 ? ' — free!' : ''}
                </strong>{' '}
                <span className="text-muted-foreground">
                  (then {formatCents(quote.base_cents)}/mo
                  {quote.first_month_only ? ', code applies to the first month only' : ''})
                </span>
              </p>
            )}
          </div>

          <div className="mt-6 border-t border-border pt-6">
            {me === null ? (
              <div className="rounded-md border border-dashed border-border p-4">
                <p className="text-sm font-medium">Log in to subscribe</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  <Link to="/login" className="underline hover:opacity-80">
                    Log in
                  </Link>{' '}
                  or{' '}
                  <Link to="/signup" className="underline hover:opacity-80">
                    sign up
                  </Link>{' '}
                  to continue to payment.
                </p>
              </div>
            ) : quote ? (
              <div className="space-y-2">
                <Button className="w-full" disabled={paying} onClick={subscribe}>
                  {paying ? 'Contacting PayPal…' : 'Continue with PayPal'}
                </Button>
                <p className="pt-1 text-xs text-muted-foreground">
                  PayPal, debit/credit cards, Google Pay and Apple Pay are all accepted
                  securely through PayPal checkout.
                </p>
              </div>
            ) : (
              <div className="rounded-md border border-dashed border-border p-4">
                <p className="text-sm font-medium">
                  {pending ? 'Loading payment options…' : 'Payments opening soon'}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {quote?.paypal?.note ??
                    (pending
                      ? 'Contacting the payment server…'
                      : 'PayPal checkout is being connected — check back shortly.')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
